import { useState } from 'react';
import Layout from '../../../components/layout/Layout';

export default function CompraPontosPage() {
  const [currentStep, setCurrentStep] = useState<'form' | 'confirmation' | 'success'>('form');
  const [formData, setFormData] = useState({
    companhia: '',
    quantidadePremiacao: '',
    tarifaPremiacao: '',
    descricaoCredito: '',
    pontosPremiacao: '',
    taxaAdministracao: '5', // Valor padrão
    pontosDisponibilizar: '',
    valorOperacao: '',
    identificacao: '',
    comentario: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.companhia) newErrors.companhia = 'Campo obrigatório';
    if (!formData.quantidadePremiacao) newErrors.quantidadePremiacao = 'Campo obrigatório';
    if (!formData.tarifaPremiacao) newErrors.tarifaPremiacao = 'Campo obrigatório';
    if (!formData.descricaoCredito) newErrors.descricaoCredito = 'Campo obrigatório';
    if (!formData.pontosPremiacao) newErrors.pontosPremiacao = 'Campo obrigatório';
    if (!formData.pontosDisponibilizar) newErrors.pontosDisponibilizar = 'Campo obrigatório';
    if (!formData.valorOperacao) newErrors.valorOperacao = 'Campo obrigatório';
    if (!formData.identificacao) newErrors.identificacao = 'Campo obrigatório';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      setCurrentStep('confirmation');
    }
  };

  const handleConfirm = () => {
    // Simular processamento da compra
    setTimeout(() => {
      setCurrentStep('success');
    }, 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleVoltar = () => {
    window.REACT_APP_NAVIGATE('/admin/incentivo/pontuacao');
  };

  const handleNovaCompra = () => {
    setFormData({
      companhia: '',
      quantidadePremiacao: '',
      tarifaPremiacao: '',
      descricaoCredito: '',
      pontosPremiacao: '',
      taxaAdministracao: '5',
      pontosDisponibilizar: '',
      valorOperacao: '',
      identificacao: '',
      comentario: ''
    });
    setErrors({});
    setCurrentStep('form');
  };

  const handleVoltarFinanceiro = () => {
    window.REACT_APP_NAVIGATE('/admin/incentivo/pontuacao');
  };

  // Função para converter string de valor para número
  const parseValueToNumber = (value: string): number => {
    if (!value) return 0;
    // Remove "R$", espaços, pontos de milhares e substitui vírgula por ponto
    const cleanValue = value.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Verificar se precisa de depósito
  const saldoContaDigital = 2450.00; // Valor da conta digital do header
  const valorOperacaoNumerico = parseValueToNumber(formData.valorOperacao);
  const precisaDeposito = valorOperacaoNumerico > saldoContaDigital;

  const handleDeposito = () => {
    // Navegar para página de depósito
    window.REACT_APP_NAVIGATE('/admin/incentivo/deposito');
  };

  // Renderizar com base na etapa atual
  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <button 
                onClick={handleVoltar}
                className="text-blue-600 hover:text-blue-800 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
              </button>
              <span className="text-sm text-gray-500">Financeiro</span>
              <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
              <span className="text-sm text-gray-900">
                {currentStep === 'form' && 'Compra de Pontos'}
                {currentStep === 'confirmation' && 'Confirmação da Compra'}
                {currentStep === 'success' && 'Compra Realizada'}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentStep === 'form' && 'Compra de Pontos'}
              {currentStep === 'confirmation' && 'Confirmar Operação'}
              {currentStep === 'success' && 'Compra Realizada com Sucesso'}
            </h1>
            <p className="text-gray-600 mt-1">
              {currentStep === 'form' && 'Preencha os dados para realizar a compra de pontos'}
              {currentStep === 'confirmation' && 'Revise os dados e confirme a operação'}
              {currentStep === 'success' && 'Sua compra de pontos foi processada com sucesso'}
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                currentStep === 'form' ? 'bg-blue-600 text-white' : 'bg-green-500 text-white'
              }`}>
                {currentStep === 'form' ? '1' : <i className="ri-check-line"></i>}
              </div>
              <span className={`font-semibold ${currentStep === 'form' ? 'text-gray-900' : 'text-green-600'}`}>
                Dados da Compra
              </span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${currentStep === 'form' ? 'bg-gray-200' : 'bg-green-500'}`}></div>
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                currentStep === 'confirmation' ? 'bg-blue-600 text-white' : 
                currentStep === 'success' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep === 'success' ? <i className="ri-check-line"></i> : '2'}
              </div>
              <span className={`font-semibold ${
                currentStep === 'confirmation' ? 'text-gray-900' : 
                currentStep === 'success' ? 'text-green-600' : 'text-gray-600'
              }`}>
                Confirmação
              </span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${currentStep === 'success' ? 'bg-green-500' : 'bg-gray-200'}`}></div>
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                currentStep === 'success' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep === 'success' ? <i className="ri-check-line"></i> : '3'}
              </div>
              <span className={`font-semibold ${currentStep === 'success' ? 'text-green-600' : 'text-gray-600'}`}>
                Finalizado
              </span>
            </div>
          </div>
        </div>

        {/* Formulário - Etapa 1 */}
        {currentStep === 'form' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Formulário Principal */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Dados da Operação</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Companhia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Companhia *
                    </label>
                    <select
                      name="companhia"
                      value={formData.companhia}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8 ${
                        errors.companhia ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Selecione a companhia</option>
                      <option value="empresa-a">Empresa A - CNPJ: 12.345.678/0001-90</option>
                      <option value="empresa-b">Empresa B - CNPJ: 98.765.432/0001-10</option>
                      <option value="empresa-c">Empresa C - CNPJ: 11.222.333/0001-44</option>
                    </select>
                    {errors.companhia && <p className="text-red-500 text-xs mt-1">{errors.companhia}</p>}
                  </div>

                  {/* Quantidade de Premiação */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantidade de Premiação *
                    </label>
                    <input
                      type="number"
                      name="quantidadePremiacao"
                      value={formData.quantidadePremiacao}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.quantidadePremiacao ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 1000"
                    />
                    {errors.quantidadePremiacao && <p className="text-red-500 text-xs mt-1">{errors.quantidadePremiacao}</p>}
                  </div>

                  {/* Tarifas de Premiação */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tarifas de Premiação *
                    </label>
                    <input
                      type="text"
                      name="tarifaPremiacao"
                      value={formData.tarifaPremiacao}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.tarifaPremiacao ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="R$ 0,00"
                    />
                    {errors.tarifaPremiacao && <p className="text-red-500 text-xs mt-1">{errors.tarifaPremiacao}</p>}
                  </div>

                  {/* Descrição de Crédito */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição de Crédito *
                    </label>
                    <input
                      type="text"
                      name="descricaoCredito"
                      value={formData.descricaoCredito}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.descricaoCredito ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Descrição do crédito"
                    />
                    {errors.descricaoCredito && <p className="text-red-500 text-xs mt-1">{errors.descricaoCredito}</p>}
                  </div>

                  {/* Pontos para Premiação */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pontos para Premiação *
                    </label>
                    <input
                      type="number"
                      name="pontosPremiacao"
                      value={formData.pontosPremiacao}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.pontosPremiacao ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 5000"
                    />
                    {errors.pontosPremiacao && <p className="text-red-500 text-xs mt-1">{errors.pontosPremiacao}</p>}
                  </div>

                  {/* Taxa de Administração */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taxa de Administração (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="taxaAdministracao"
                      value={formData.taxaAdministracao}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="5.00"
                    />
                  </div>

                  {/* Pontos a Disponibilizar */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pontos a Disponibilizar *
                    </label>
                    <input
                      type="number"
                      name="pontosDisponibilizar"
                      value={formData.pontosDisponibilizar}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.pontosDisponibilizar ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Ex: 4750"
                    />
                    {errors.pontosDisponibilizar && <p className="text-red-500 text-xs mt-1">{errors.pontosDisponibilizar}</p>}
                  </div>

                  {/* Valor da Operação */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor da Operação *
                    </label>
                    <input
                      type="text"
                      name="valorOperacao"
                      value={formData.valorOperacao}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.valorOperacao ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="R$ 0,00"
                    />
                    {errors.valorOperacao && <p className="text-red-500 text-xs mt-1">{errors.valorOperacao}</p>}
                  </div>

                  {/* Identificação */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Identificação *
                    </label>
                    <input
                      type="text"
                      name="identificacao"
                      value={formData.identificacao}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                        errors.identificacao ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Identificação da operação"
                    />
                    {errors.identificacao && <p className="text-red-500 text-xs mt-1">{errors.identificacao}</p>}
                  </div>

                  {/* Comentário */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comentário
                    </label>
                    <textarea
                      name="comentario"
                      value={formData.comentario}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                      placeholder="Comentários adicionais sobre a operação..."
                    ></textarea>
                    <div className="text-xs text-gray-500 mt-1">
                      {formData.comentario.length}/500 caracteres
                    </div>
                  </div>
                </div>

                {/* Botões de Ação */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                  <button 
                    type="button"
                    onClick={handleVoltar}
                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Voltar
                  </button>
                  <div className="flex items-center space-x-3">
                    {precisaDeposito && formData.valorOperacao && (
                      <button 
                        type="button"
                        onClick={handleDeposito}
                        className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer flex items-center space-x-2"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-bank-line"></i>
                        </div>
                        <span>Depósito</span>
                      </button>
                    )}
                    <button 
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Sidebar com Resumo */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo da Operação</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Quantidade:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formData.quantidadePremiacao || '0'} pontos
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Taxa Admin:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formData.taxaAdministracao || '0'}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">A Disponibilizar:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formData.pontosDisponibilizar || '0'} pontos
                    </span>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between">
                      <span className="text-base font-semibold text-gray-900">Valor Total:</span>
                      <span className="text-base font-bold text-blue-600">
                        {formData.valorOperacao || 'R$ 0,00'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Alerta de Saldo Insuficiente */}
                {precisaDeposito && formData.valorOperacao && (
                  <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-alert-line text-orange-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-orange-900 mb-1">Saldo Insuficiente</h4>
                        <p className="text-xs text-orange-800 mb-2">
                          O valor da operação ({formData.valorOperacao}) é superior ao saldo da sua conta digital (R$ {saldoContaDigital.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}).
                        </p>
                        <p className="text-xs text-orange-700">
                          Realize um depósito antes de continuar com a compra.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Informações Importantes */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Informações Importantes</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• A operação será processada em até 24 horas</li>
                    <li>• Os pontos serão creditados automaticamente</li>
                    <li>• Taxa de administração será descontada</li>
                    <li>• Operação sujeita à aprovação</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmação - Etapa 2 */}
        {currentStep === 'confirmation' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-alert-line text-yellow-600 text-2xl"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Confirmar Compra de Pontos</h3>
                <p className="text-gray-600">Revise os dados da operação antes de confirmar</p>
              </div>

              {/* Detalhes da Operação */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Dados da Empresa</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Companhia:</span>
                      <span className="font-medium text-gray-900">{formData.companhia}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Identificação:</span>
                      <span className="font-medium text-gray-900">{formData.identificacao}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Detalhes da Operação</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quantidade de Premiação:</span>
                      <span className="font-medium text-gray-900">{formData.quantidadePremiacao} pontos</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pontos para Premiação:</span>
                      <span className="font-medium text-gray-900">{formData.pontosPremiacao} pontos</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Taxa de Administração:</span>
                      <span className="font-medium text-gray-900">{formData.taxaAdministracao}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pontos a Disponibilizar:</span>
                      <span className="font-medium text-gray-900">{formData.pontosDisponibilizar} pontos</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo Financeiro */}
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Resumo Financeiro</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Tarifas de Premiação</p>
                    <p className="text-xl font-bold text-gray-900">{formData.tarifaPremiacao}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Descrição de Crédito</p>
                    <p className="text-xl font-bold text-gray-900">{formData.descricaoCredito}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Valor Total da Operação</p>
                    <p className="text-2xl font-bold text-blue-600">{formData.valorOperacao}</p>
                  </div>
                </div>
              </div>

              {/* Comentário */}
              {formData.comentario && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Comentário</h4>
                  <p className="text-sm text-blue-800">{formData.comentario}</p>
                </div>
              )}

              {/* Termos e Condições */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-information-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-yellow-900 mb-2">Termos da Operação</h4>
                    <ul className="text-sm text-yellow-800 space-y-1">
                      <li>• A operação será processada em até 24 horas úteis</li>
                      <li>• Os pontos serão creditados automaticamente após aprovação</li>
                      <li>• A taxa de administração será descontada do valor total</li>
                      <li>• Esta operação está sujeita à aprovação interna</li>
                      <li>• O cancelamento poderá ser solicitado em até 2 horas após a confirmação</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => setCurrentStep('form')}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                  <span>Voltar e Editar</span>
                </button>
                <button 
                  onClick={handleConfirm}
                  className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2 text-lg font-semibold"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line"></i>
                  </div>
                  <span>Confirmar Compra</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sucesso - Etapa 3 */}
        {currentStep === 'success' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-check-line text-green-600 text-4xl"></i>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Compra Realizada com Sucesso!</h3>
              <p className="text-gray-600 mb-8">
                Sua solicitação de compra de pontos foi processada e será analisada pela equipe financeira.
              </p>

              {/* Detalhes da Operação */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">Protocolo:</span>
                    <span className="font-bold text-green-900">#{Math.random().toString(36).substr(2, 9).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Data/Hora:</span>
                    <span className="font-bold text-green-900">{new Date().toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Pontos Solicitados:</span>
                    <span className="font-bold text-green-900">{formData.pontosDisponibilizar}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Valor:</span>
                    <span className="font-bold text-green-900">{formData.valorOperacao}</span>
                  </div>
                </div>
              </div>

              {/* Próximos Passos */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-left">
                <h4 className="text-sm font-semibold text-blue-900 mb-3">Próximos Passos</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                    <span className="text-sm text-blue-800">Análise da solicitação (até 4 horas)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                    <span className="text-sm text-blue-800">Processamento do pagamento (até 24 horas)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                    <span className="text-sm text-blue-800">Crédito automático dos pontos</span>
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex items-center justify-center space-x-4">
                <button 
                  onClick={handleVoltarFinanceiro}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voltar ao Financeiro
                </button>
                <button 
                  onClick={handleNovaCompra}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Nova Compra
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}